/* eslint arrow-body-style: */
import {
	addCartItem as apiAddCartItem,
	deleteCart as apiDeleteCart,
	deleteCartItem as apiDeleteCartItem,
	getCartItems as apiGetCartItems,
	updateCartItem as apiUpdateCartItem,
	emailCart as apiEmailCart,
} from 'api/Cart';

import { isCancel } from 'api/Helpers';

import {
	CLEAR_UPDATE_MESSAGES,
	IS_FETCHING,
	IS_UPDATING,
	FETCH_ERROR,
	RECEIVE_SHOPPING_CART,
	RECEIVE_UPDATE_MESSAGES,
	RECEIVE_WISH_LIST,
	UPDATE_ERROR,
	SHOPPING_CART,
	IS_EMAILINGCART,
	EMAILINGCART_ERROR,
	RECEIVE_EMAILINGCART_MESSAGES,
	CLEAR_EMAILCART_MESSAGES,
} from './constants';

/**
 * Clear the messages saved from the last cart/idea board update.
 */
const clearUpdateMessages = () => ({
	type: CLEAR_UPDATE_MESSAGES,
});

/**
 * Store a shopping cart in thestore.
 * @param {Ojbect} cart shopping cart object with cart items
 */
const receiveShoppingCart = cart => ({
	type: RECEIVE_SHOPPING_CART,
	cart,
});

/**
 * Save errors/warnings returned from eSTORIS when trying to update/add/delete
 * a cart item. For no errors this will be an empty array.
 * Note: To store errors like network connection errors use updateUpdateError
 * which accepts an exception object.
 * @param {array} messages
 */
const receiveUpdateMessages = messages => ({
	type: RECEIVE_UPDATE_MESSAGES,
	messages,
});

/**
 * Store an idea board in the store.
 * @param {Object} cart
 */
const receiveWishList = cart => ({
	type: RECEIVE_WISH_LIST,
	cart,
});

/**
 * Error received from the cart fetch.
 * @param {error} fetchError
 */
const updateFetchError = fetchError => ({
	type: FETCH_ERROR,
	fetchError,
});

/**
 * Indicates that we are fetching the cart from the database.
 * @param {boolean} isFetching
 */
const updateFetchStatus = isFetching => ({
	type: IS_FETCHING,
	isFetching,
});

/**
 * Error received from cart update.
 * Note: this deals with things like network errors
 * hence why it is an error object. For errors/warnings received from eSTORIS
 * explaining why the user cannot add/update/delete a cart use receiveUpdateErrors
 * @param {error} updateError
 */
const updateUpdateError = updateError => ({
	type: UPDATE_ERROR,
	updateError,
});

/**
 * Indicates that we are updating the shopping cart.
 * @param {boolean} isUpdating
 */
const updateUpdateStatus = isUpdating => ({
	type: IS_UPDATING,
	isUpdating,
});

/**
 * Error received from the email cart.
 * @param {error} emailCartError
 */
const updateEmailCartError = emailCartError => ({
	type: EMAILINGCART_ERROR,
	emailCartError,
});

/**
 * Indicates that we are email the cart.
 * @param {boolean} isEmailingCart
 */
const updateEmailCartStatus = isEmailingCart => ({
	type: IS_EMAILINGCART,
	isEmailingCart,
});

const receiveEmailCartMessages = messages => ({
	type: RECEIVE_EMAILINGCART_MESSAGES,
	messages,
});

const clearEmailCartMessages = () => ({
	type: CLEAR_EMAILCART_MESSAGES,
});
// Thunks

/**
 * Retrieve a shopping cart or idea board for a user.
 * @param {axios cancel token} cancelToken
 * @param {number} customerId integer representing the NOP customer id
 * @param {number} cartType integer
 * 			value: 1 for shopping cart or
 *             2 for wishlist
 * @param {boolean} requestSync request a STORIS -> eSTORIS sync before retrieving the cart
 * 			This option is mutually exclusive with Wishlists since wishlists cannot be synced.
 *      This can be time consuming so should only be specified on a first fetch.
 * @param {boolean} requestSync
 */
const getCartItems = (cancelToken, customerId, cartType, requestSync) => {
	return dispatch => {
		dispatch(updateFetchStatus(true));
		return apiGetCartItems(cancelToken, customerId, cartType, requestSync)
			.then(response => {
				dispatch(updateFetchStatus(false));

				if (
					response.data.results.cartType === SHOPPING_CART ||
					response.data.results.cartType === 'ShoppingCart'
				) {
					dispatch(receiveShoppingCart(response.data.results));
				} else {
					dispatch(receiveWishList(response.data.results));
				}
			})
			.catch(error => {
				dispatch(updateFetchStatus(false));
				if (isCancel(error)) {
					// Request was canceled.
					return;
				}

				dispatch(updateFetchError(error));
			});
	};
};

/**
 * Add a single item to a user's shopping cart or idea board.
 * This routine will refetch after the request completes.
 * @param {axios cancel token} cancelToken
 * @param {number} customerId
 * @param {number} productId
 * @param {selectedStoreId} selectedStoreId
 * @param {number} cartType 1: shopping cart / 2: idea board
 * @param {CartItem} cartItem
 */
const addCartItem = (cancelToken, customerId, productId, selectedStoreId, cartType, cartItem) => {
	return dispatch => {
		dispatch(updateUpdateStatus(true));

		return apiAddCartItem(cancelToken, customerId, productId, selectedStoreId, cartType, cartItem)
			.then(response => {
				dispatch(updateUpdateStatus(false));
				// eSTORIS returns a list of errors if our request does not meet all the business
				// rules. If the request was successfull we get back an empty array of errors.
				const messages = response.data.errors;
				if (Array.isArray(messages) && messages.length === 0) {
					const cartName = cartType === SHOPPING_CART ? 'shopping cart' : 'idea board';
					const message = `Item added to ${cartName}.`;
					messages.push(message);
				}
				dispatch(receiveUpdateMessages(messages));

				// Multiple items may have been added if this was a grouped item. Refetch so our cart
				// is current.
				dispatch(getCartItems(cancelToken, customerId, cartType));
			})
			.catch(error => {
				dispatch(updateUpdateStatus(false));
				if (isCancel(error)) {
					// Request was canceled.
					return;
				}

				dispatch(updateUpdateError(error));
			});
	};
};

/**
 * Delete a user's shopping cart or idea board.
 * @param {axios cancel token} cancelToken
 * @param {number} customerId - integer (required),
 * @param {number} cartType - integer (required) 1: shopping cart / 2: wishlist
 */
const deleteCart = (cancelToken, customerId, cartType) => {
	return dispatch => {
		dispatch(updateUpdateStatus(true));

		return apiDeleteCart(cancelToken, customerId, cartType)
			.then(response => {
				dispatch(updateUpdateStatus(false));
				return response;
			})
			.catch(error => {
				dispatch(updateUpdateStatus(false));
				if (isCancel(error)) {
					// Request was canceled.
					return;
				}

				dispatch(updateUpdateError(error));
			});
	};
};

/**
 * Delete a single item from a shopping cart or idea board.
 * This routine will refetch after the request completes.
 * @param {axios cancel token} cancelToken
 * @param {number} customerId - integer (required),
 * @param {number} cartId - integer (required),
 * @param {number} cartType - integer (required) 1: shopping cart / 2: wishlist
 * 	not needed to delete an item but required to trigger a refetch.
 */
const deleteCartItem = (cancelToken, customerId, cartId, cartType) => {
	return dispatch => {
		dispatch(updateUpdateStatus(true));

		return apiDeleteCartItem(cancelToken, customerId, cartId, cartType)
			.then(response => {
				dispatch(updateUpdateStatus(false));

				const cartName = cartType === SHOPPING_CART ? 'shopping cart' : 'idea board';
				const message = response.data.deleted
					? `Item deleted from ${cartName}.`
					: `Item could not be deleted from ${cartName}.`;
				// Expects an array
				dispatch(receiveUpdateMessages([message]));

				dispatch(getCartItems(cancelToken, customerId, cartType));
				return response;
			})
			.catch(error => {
				dispatch(updateUpdateStatus(false));
				if (isCancel(error)) {
					// Request was canceled.
					return;
				}

				dispatch(updateUpdateError(error));
			});
	};
};

/**
 * Update a single cart item in a user's shopping cart or idea board.
 * Note: setting the quantity to zero will delete the item.
 * This routine will refetch after the request completes.
 * @param {axios cancel token} cancelToken
 * @param {number} customerId integer representing the customer id
 * @param {CartItem} cartItem
 * @param {number} cartType 1: shopping cart, 2: idea board
 */
const updateCartItem = (cancelToken, customerId, cartItem, cartType) => {
	return dispatch => {
		dispatch(updateUpdateStatus(true));

		return apiUpdateCartItem(cancelToken, customerId, cartItem)
			.then(response => {
				dispatch(updateUpdateStatus(false));
				// eSTORIS returns a list of errors if our request does not meet all the business
				// rules. If the request was successfull we get back an empty array of errors.
				const messages = response.data.errors;
				if (Array.isArray(messages) && messages.length === 0) {
					const cartName = cartType === SHOPPING_CART ? 'Shopping cart' : 'Idea board';
					const message = `${cartName} item updated.`;
					messages.push(message);
				}
				dispatch(receiveUpdateMessages(messages));

				dispatch(getCartItems(cancelToken, customerId, cartType));
			})
			.catch(error => {
				dispatch(updateUpdateStatus(false));
				if (isCancel(error)) {
					// Request was canceled.
					return;
				}

				dispatch(updateUpdateError(error));
			});
	};
};

/**
 * Email cart items in a user's shopping cart.
 * Note: setting the quantity to zero will delete the item.
 * This routine will refetch after the request completes.
 * @param {axios cancel token} cancelToken
 * @param {number} customerId integer representing the customer id
 * @param {string} alternateEmail
 */
const emailCart = (cancelToken, customerId, alternateEmail, customerEmail, customerName) => {
	return dispatch => {
		dispatch(updateEmailCartStatus(true));
		dispatch(receiveEmailCartMessages(undefined));
		return apiEmailCart(cancelToken, customerId, alternateEmail, customerEmail, customerName)
			.then(() => {
				dispatch(updateEmailCartStatus(false));
				dispatch(receiveEmailCartMessages('Your shopping cart has been sent to the entered email'));
			})
			.catch(error => {
				dispatch(updateEmailCartStatus(false));
				if (isCancel(error)) {
					// Request was canceled.
					return;
				}

				dispatch(updateEmailCartError(error));
			});
	};
};

export {
	addCartItem,
	clearUpdateMessages,
	deleteCart,
	deleteCartItem,
	getCartItems,
	receiveShoppingCart,
	receiveUpdateMessages,
	receiveWishList,
	updateCartItem,
	emailCart,
	updateFetchError,
	updateFetchStatus,
	updateUpdateError,
	updateUpdateStatus,
	updateEmailCartError,
	updateEmailCartStatus,
	receiveEmailCartMessages,
	clearEmailCartMessages,
};
